import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getSelectedOrgId } from "@/lib/authenticatedFetch";
import { listTables as listTablesAPI, getTableSchema as getTableSchemaAPI, executeQuery as executeQueryAPI, type TableInfo, type ColumnInfo, type QueryResult } from "@/lib/api/org-database";
export const orgDatabaseKeys = {
  all: ["orgDatabase"] as const,
  tables: (orgId: string) => [...orgDatabaseKeys.all, orgId, "tables"] as const,
  tableSchema: (orgId: string, tableName: string) => [...orgDatabaseKeys.all, orgId, "schema", tableName] as const
};
export function useOrgTables(options) {
  const $ = _c(6);
  let orgId;
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    orgId = getSelectedOrgId();
    t1 = useQuery;
    t0 = orgDatabaseKeys.tables(orgId ?? "");
    t2 = async () => {
      if (!orgId) {
        throw new Error("No organization selected");
      }
      const response = await listTablesAPI(orgId);
      return response.data.tables;
    };
    $[0] = orgId;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    orgId = $[0];
    t0 = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = !!orgId && options?.enabled !== false;
  let t4;
  if ($[4] !== t3) {
    t4 = {
      queryKey: t0,
      queryFn: t2,
      enabled: t3
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t1(t4);
}
export function useTableSchema(tableName) {
  const $ = _c(11);
  let orgId;
  let t0;
  let t1;
  if ($[0] !== tableName) {
    orgId = getSelectedOrgId();
    t1 = useQuery;
    t0 = orgDatabaseKeys.tableSchema(orgId ?? "", tableName);
    $[0] = tableName;
    $[1] = orgId;
    $[2] = t0;
    $[3] = t1;
  } else {
    orgId = $[1];
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== orgId || $[5] !== tableName) {
    t2 = async () => {
      if (!orgId) {
        throw new Error("No organization selected");
      }
      const response = await getTableSchemaAPI(orgId, tableName);
      return response.data.columns;
    };
    $[4] = orgId;
    $[5] = tableName;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = !!orgId && !!tableName;
  let t4;
  if ($[7] !== t0 || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      queryKey: t0,
      queryFn: t2,
      enabled: t3
    };
    $[7] = t0;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t1(t4);
}
export function useExecuteQuery() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getSelectedOrgId();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const orgId = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async t2 => {
      const {
        sql,
        params
      } = t2;
      if (!orgId) {
        throw new Error("No organization selected");
      }
      const response = await executeQueryAPI(orgId, sql, params);
      return response.data;
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== queryClient) {
    t2 = {
      mutationFn: t1,
      onSuccess: () => {
        if (orgId) {
          queryClient.invalidateQueries({
            queryKey: orgDatabaseKeys.tables(orgId)
          });
        }
      }
    };
    $[2] = queryClient;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return useMutation(t2);
}